import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { DOCUMENT_CATEGORIES } from '../constants';
import Dropdown from './Dropdown';

const newDocId = () => `new_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
const emptyDoc = () => ({ _id: newDocId(), document_type: '', document_name: '', google_drive_link: '', expiry_date: '' });

const DocumentSubsection = ({
  documents = [],
  onChange,
  disabled = false,
}) => {
  const update = (index, field, value) => {
    const updated = documents.map((doc, i) => i === index ? { ...doc, [field]: value } : doc);
    onChange(updated);
  };

  const addRow = () => onChange([...documents, emptyDoc()]);

  const removeRow = (index) => onChange(documents.filter((_, i) => i !== index));

  return (
    <div className="space-y-3" data-testid="documents-subsection">
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
        {!disabled && (
          <button
            type="button"
            onClick={addRow}
            data-testid="documents-subsection-add"
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800 font-medium"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Document
          </button>
        )}
      </div>

      {/* New document rows */}
      {documents.map((doc, index) => (
        <div key={doc._id ?? index} className="border border-gray-200 rounded-md p-3 space-y-2 bg-white">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 grid grid-cols-2 gap-2">
              {/* Document Type */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                {/* One of the fixed categories; the server refuses anything else. */}
                <Dropdown
                  value={doc.document_type}
                  onChange={e => update(index, 'document_type', e.target.value)}
                  disabled={disabled}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                >
                  <option value="">Select type...</option>
                  {DOCUMENT_CATEGORIES.map(t => <option key={t} value={t}>{t}</option>)}
                </Dropdown>
              </div>

              {/* Document Name */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={doc.document_name}
                  onChange={e => update(index, 'document_name', e.target.value)}
                  disabled={disabled}
                  placeholder="e.g. John's CNIC"
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                />
              </div>
            </div>

            {/* Remove button */}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="mt-5 text-gray-400 hover:text-red-500 flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Google Drive Link */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Google Drive Link <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={doc.google_drive_link}
              onChange={e => update(index, 'google_drive_link', e.target.value)}
              disabled={disabled}
              placeholder="https://drive.google.com/..."
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
            />
          </div>

          {/* Expiry Date */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Expiry Date (optional)</label>
            <input
              type="date"
              value={doc.expiry_date}
              onChange={e => update(index, 'expiry_date', e.target.value)}
              disabled={disabled}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
            />
          </div>
        </div>
      ))}

      {documents.length === 0 && (
        <p className="text-sm text-gray-400 italic">No documents attached.</p>
      )}
    </div>
  );
};

export default DocumentSubsection;
